import { IntlShape } from '@cmg/i18n';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';

import { ConvertibleFilingTalkRanges } from '../../../common/hooks/useConvertibleFilingTalkRanges';
import { InterestLevelInterestUnit, InterestLevelLimitType, OfferingType } from '../../../graphql';
import {
  ConvertibleOrderType,
  getConvertibleOrderType,
} from '../../offering-side-panel/order-book/indication-activity/components/manage-indication/common/utils/getConvertibleOrderType';

type InterestLevel = {
  interestQuantity?: number | null;
  interestUnit: InterestLevelInterestUnit;
  limitPrice?: number | null;
  limitType?: InterestLevelLimitType | null;
  coupon?: number | null;
  premium?: number | null;
  percentageOfPar?: number | null;
};

type FormatInterestLevelProps = Readonly<{
  intl: IntlShape;
  interestLevel: InterestLevel;
  pricingCurrencyCode: string;
  demandCurrencyCode: string | null | undefined;
  offeringType: OfferingType | null | undefined;
  convertibleTalkRanges: ConvertibleFilingTalkRanges;
  isIndicationPriceAsPercentageParEnabled: boolean;
  hideConvertibleNonMarketOrderType?: boolean;
}>;

export const formatInterestLevel = ({
  intl,
  interestLevel,
  demandCurrencyCode,
  pricingCurrencyCode,
  offeringType,
  convertibleTalkRanges,
  isIndicationPriceAsPercentageParEnabled,
  hideConvertibleNonMarketOrderType,
}: FormatInterestLevelProps) => {
  const priceLabel = formatInterestLevelPrice({
    intl,
    interestLevel,
    demandCurrencyCode,
    pricingCurrencyCode,
    offeringType,
    convertibleTalkRanges,
    isIndicationPriceAsPercentageParEnabled,
    hideConvertibleNonMarketOrderType,
  });
  const quantityLabel = formatInterestLevelQuantity({
    intl,
    interestLevel,
    demandCurrencyCode,
    pricingCurrencyCode,
  });

  return `${quantityLabel} @ ${priceLabel}`;
};

type FormatInterestLimitPriceProps = Pick<
  FormatInterestLevelProps,
  | 'intl'
  | 'interestLevel'
  | 'demandCurrencyCode'
  | 'pricingCurrencyCode'
  | 'offeringType'
  | 'convertibleTalkRanges'
  | 'isIndicationPriceAsPercentageParEnabled'
  | 'hideConvertibleNonMarketOrderType'
>;

export const formatInterestLevelPrice = ({
  intl,
  interestLevel: { limitPrice, limitType, coupon, premium, percentageOfPar },
  demandCurrencyCode,
  pricingCurrencyCode,
  offeringType,
  convertibleTalkRanges,
  isIndicationPriceAsPercentageParEnabled,
  hideConvertibleNonMarketOrderType,
}: FormatInterestLimitPriceProps) => {
  if (offeringType === OfferingType.Convertible) {
    return formatConvertibleInterestLevelPrice({
      intl,
      interestLevel: { coupon, premium, percentageOfPar },
      convertibleTalkRanges,
      isIndicationPriceAsPercentageParEnabled,
      hideConvertibleNonMarketOrderType,
    });
  }

  if (isNil(limitPrice)) {
    return 'Market';
  }

  if (limitType === InterestLevelLimitType.Discount) {
    return `Discount ${intl.formatNumber(limitPrice, { format: 'percent' })}`;
  } else if (limitType === InterestLevelLimitType.Premium) {
    return `Premium ${intl.formatNumber(limitPrice, { format: 'percent' })}`;
  }
  return intl.formatNumber(limitPrice, {
    format: 'currency',
    currency: defaultTo(demandCurrencyCode, pricingCurrencyCode),
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};

type FormatInterestQuantityProps = Pick<
  FormatInterestLevelProps,
  'interestLevel' | 'demandCurrencyCode' | 'pricingCurrencyCode' | 'intl'
>;

export const formatInterestLevelQuantity = ({
  intl,
  interestLevel: { interestQuantity, interestUnit },
  demandCurrencyCode,
  pricingCurrencyCode,
}: FormatInterestQuantityProps) => {
  switch (interestUnit) {
    case InterestLevelInterestUnit.Percent:
      return intl.formatNumber(interestQuantity, { format: 'percent' });
    case InterestLevelInterestUnit.Shares:
      return `${intl.formatNumber(interestQuantity, { format: 'integer' })} Shares`;
    case InterestLevelInterestUnit.Bonds:
      return `${intl.formatNumber(interestQuantity, { format: 'integer' })} Bonds`;
    case InterestLevelInterestUnit.Currency:
      return intl.formatNumber(interestQuantity, {
        format: 'currency',
        currency: defaultTo(demandCurrencyCode, pricingCurrencyCode),
      });
    default:
      return '-';
  }
};

type FormatConvertibleInterestLevelPriceProps = {
  intl: IntlShape;
  interestLevel: Pick<InterestLevel, 'coupon' | 'premium' | 'percentageOfPar'>;
  convertibleTalkRanges: ConvertibleFilingTalkRanges;
  isIndicationPriceAsPercentageParEnabled: boolean;
  hideConvertibleNonMarketOrderType?: boolean;
};

export function formatConvertibleInterestLevelPrice({
  intl,
  interestLevel: { coupon, premium, percentageOfPar },
  convertibleTalkRanges,
  isIndicationPriceAsPercentageParEnabled,
  hideConvertibleNonMarketOrderType = false,
}: FormatConvertibleInterestLevelPriceProps) {
  if (isNil(convertibleTalkRanges)) {
    return `Custom ${formatConvertibleLimitPriceConfiguration(intl, {
      coupon,
      premium,
      percentageOfPar,
    })}`;
  }

  const convertibleOrderType = getConvertibleOrderType({
    coupon: defaultTo(coupon, null),
    premium: defaultTo(premium, null),
    percentageOfPar: defaultTo(percentageOfPar, null),
    couponTalkLow: convertibleTalkRanges.couponTalkLow,
    couponTalkHigh: convertibleTalkRanges.couponTalkHigh,
    premiumTalkLow: convertibleTalkRanges.premiumTalkLow,
    premiumTalkHigh: convertibleTalkRanges.premiumTalkHigh,
    priceAsOfPar: convertibleTalkRanges.priceAsOfPar,
    isIndicationPriceAsPercentageParEnabled,
  });

  if (convertibleOrderType === ConvertibleOrderType.Market) {
    return 'Market';
  }

  const limitPriceConfiguration = formatConvertibleLimitPriceConfiguration(intl, {
    coupon,
    premium,
    percentageOfPar,
  });

  if (hideConvertibleNonMarketOrderType) {
    return limitPriceConfiguration;
  }

  return `${convertibleOrderType} ${limitPriceConfiguration}`;
}

export type FormatConvertibleLimitPriceConfigurationProps = Pick<
  InterestLevel,
  'coupon' | 'premium' | 'percentageOfPar'
>;

export function formatConvertibleLimitPriceConfiguration(
  intl: IntlShape,
  { coupon, premium, percentageOfPar }: FormatConvertibleLimitPriceConfigurationProps
) {
  const limitPriceParts: string[] = [];

  if (!isNil(coupon)) {
    limitPriceParts.push(
      `${intl.formatNumber(coupon, {
        format: 'percent',
        maximumFractionDigits: 4,
        minimumFractionDigits: 4,
      })} Coupon`
    );
  }
  if (!isNil(premium)) {
    limitPriceParts.push(`${intl.formatNumber(premium, { format: 'percent' })} Premium`);
  }
  if (!isNil(percentageOfPar)) {
    limitPriceParts.push(`${intl.formatNumber(percentageOfPar, { format: 'percent' })} of Par`);
  }
  return limitPriceParts.join(' & ');
}
